/**
 * RogerDoc Dashboard API Service
 *
 * Wrapper for making API calls to RogerDoc dashboard endpoints via the passthrough.
 * Uses the same generated client as the rest of the application for consistent authentication.
 */

import { API_BASE_URL, client } from './api';
import type {
  AdoptionReportRawData,
  BranchReportResponse,
} from '../types/adoptionReport.types';
import { QAVisit, VisitDataResponse } from '../types/visitData.types';
import type {
  CaseMixData,
  CodingTags,
  PrimaryDiagnosis,
  SecondaryDiagnoses,
} from '../types/codingData.types';
import { mapQAStatusFilterToBackend } from '../utils/qaStatusUtils';
import type {
  SavedDiagnosisState,
  CreateStateRequest,
  PushCodingStatusResponse,
} from '@/types/diagnosisState.types';

// Types based on dashboard API responses
export interface RogerDocUser {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  clinician_title: string;
  is_active?: boolean;
}

export interface RogerDocActiveUser {
  username: string;
  first_name: string;
  last_name: string;
}

export interface RogerDocVisitStats {
  OASIS: number;
  ROUTINE: number;
}

export interface RogerDocTimeSavings {
  avg_oasis_time_saving: number;
  avg_routine_time_saving: number;
}

export interface RogerDocTopSavingsClinic {
  clinician_name: string;
  total_time_saved: number;
  avg_oasis_time_saving: number;
  avg_routine_time_saving: number;
}

export interface RogerDocClinicianUsageData {
  first_name: string;
  last_name: string;
  username: string;
  clinician_title: string;
  '30_days': {
    total_visits: number;
    oasis_visits: number;
    routine_visits: number;
    total_saving: number;
    oasis_saving: number;
    routine_saving: number;
    avg_time_saving: number;
    avg_oasis_time_saving: string;
    avg_routine_time_saving: string;
    oasis_visits_threshold_count: number;
    routine_visits_threshold_count: number;
  };
  '60_days': {
    total_visits: number;
    oasis_visits: number;
    routine_visits: number;
    total_saving: number;
    oasis_saving: number;
    routine_saving: number;
    avg_time_saving: number;
    avg_oasis_time_saving: string;
    avg_routine_time_saving: string;
    oasis_visits_threshold_count: number;
    routine_visits_threshold_count: number;
  };
  all_time: {
    total_visits: number;
    oasis_visits: number;
    routine_visits: number;
    total_saving: number;
    oasis_saving: number;
    routine_saving: number;
    avg_time_saving: number;
    avg_oasis_time_saving: string;
    avg_routine_time_saving: string;
    oasis_visits_threshold_count: number;
    routine_visits_threshold_count: number;
  };
}

export interface RogerDocUsageTimeRange {
  total_visits: number;
  oasis_visits: number;
  routine_visits: number;
  avg_oasis_time_saving: number;
  avg_routine_time_saving: number;
}

export interface RogerDocUsageByClinic {
  username: string;
  first_name: string;
  last_name: string;
  clinician_title: string;
  all_time: RogerDocUsageTimeRange;
  '30_days': RogerDocUsageTimeRange;
  '60_days': RogerDocUsageTimeRange;
}

export interface RogerDocUsageResponse {
  usage_by_clinician: RogerDocUsageByClinic[];
  calculated_at: string;
}

export interface AddUserData {
  first_name: string;
  last_name: string;
  email: string;
  clinician_title: string;
}

export interface AddUserResponse {
  username: string;
  password: string;
}

export interface AddUserResponseWithData extends AddUserResponse {
  first_name: string;
  last_name: string;
  email: string;
  clinician_title: string;
}

// API Functions

/**
 * Get all clinicians/users
 */
export const getAllUsers = async (): Promise<RogerDocUser[]> => {
  const response = await client.request({
    method: 'POST',
    url: '/rogerdoc/api/dashboard/clinicians',
    throwOnError: true,
  });
  return response.data as RogerDocUser[];
};

/**
 * Get active users in the last N days
 */
export const getActiveUsers = async (
  days: number = 30
): Promise<RogerDocActiveUser[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/activeusers',
    query: { days },
    throwOnError: true,
  });
  return response.data as RogerDocActiveUser[];
};

/**
 * Get visit statistics
 */
export const getVisitStats = async (
  days: number = -1
): Promise<RogerDocVisitStats> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/visits_stats',
    query: { days },
    throwOnError: true,
  });
  if (!response.data) throw new Error('Failed to load visit stats');
  const data = response.data as {
    total_visits: number;
    completed_visits: number;
    pending_visits: number;
    OASIS: number;
    ROUTINE: number;
  };
  return {
    OASIS: data.OASIS,
    ROUTINE: data.ROUTINE,
  };
};

/**
 * Get time savings statistics
 */
export const getTimeSavings = async (
  days: number = -1
): Promise<{
  total_savings: number;
  average_savings: number;
  unit: string;
  avg_oasis_time_saving: number;
  avg_routine_time_saving: number;
}> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/time_savings_stats',
    query: { days },
    throwOnError: true,
  });
  return response.data as {
    total_savings: number;
    average_savings: number;
    unit: string;
    avg_oasis_time_saving: number;
    avg_routine_time_saving: number;
  };
};

/**
 * Get top 10 clinicians by time savings
 */
export const getTopSavingsClinicians = async (
  days: number = 30
): Promise<RogerDocTopSavingsClinic[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/get_top10_time_savings_clinicians',
    query: { days },
    throwOnError: true,
  });

  // The API returns clinician data with time periods
  if (!response.data || typeof response.data !== 'object') {
    throw new Error('Failed to load top savings clinicians');
  }
  const data = response.data as Record<string, RogerDocClinicianUsageData>;
  const clinicianData = Object.values(data);

  // Transform to the expected format using 30-day data
  const topSavingsClinicians: RogerDocTopSavingsClinic[] = clinicianData.map(
    (clinician: RogerDocClinicianUsageData) => {
      return {
        clinician_name: `${clinician.first_name} ${clinician.last_name}`,
        total_time_saved: clinician['30_days'].total_saving,
        avg_oasis_time_saving: parseFloat(
          clinician['30_days'].avg_oasis_time_saving
        ),
        avg_routine_time_saving: parseFloat(
          clinician['30_days'].avg_routine_time_saving
        ),
      };
    }
  );

  // Sort by total time saved (descending)
  topSavingsClinicians.sort((a, b) => b.total_time_saved - a.total_time_saved);
  return topSavingsClinicians;
};

/**
 * Completed visit from the /get/completed_visits endpoint
 */
export interface CompletedVisit {
  mrn?: string | null;
  clinician_name?: string | null;
  clinician_role?: string | null;
  visit_type?: string | null;
  target_date?: string | null;
  completed_date?: string | null;
}

/** Convert YYYY-MM-DD to MM/DD/YYYY */
function toMDY(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${m}/${d}/${y}`;
}

/**
 * Get completed visits for the current agency within a date range.
 * Agency ID is resolved server-side from the auth context.
 */
export const getCompletedVisits = async (
  filterStartDate: string,
  filterEndDate: string
): Promise<CompletedVisit[]> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/completed_visits',
    query: {
      filter_start_date: toMDY(filterStartDate),
      filter_end_date: toMDY(filterEndDate),
    },
  });
  // Response is string[][] with order: [visit_type, clinician_name, role, mrn, target_date, completed_date]
  const rows = response.data as string[][];
  return rows.map(row => ({
    visit_type: row[0] ?? null,
    clinician_name: row[1] ?? null,
    clinician_role: row[2] ?? null,
    mrn: row[3] ?? null,
    target_date: row[4] ?? null,
    completed_date: row[5] ?? null,
  }));
};

/**
 * Get usage statistics
 */
export const getUsageStats = async (): Promise<RogerDocUsageResponse> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/dashboard/usage',
    throwOnError: true,
  });
  if (!response.data) throw new Error('Failed to load usage stats');
  return response.data as RogerDocUsageResponse;
};

/**
 * Add a new user
 */
export const addUser = async (
  userData: AddUserData
): Promise<AddUserResponse> => {
  const response = await client.request({
    method: 'POST',
    url: '/rogerdoc/api/dashboard/clinicians/create',
    body: {
      data: userData,
      autoadd_visit_types: 'all',
    },
    throwOnError: true,
  });
  return response.data as AddUserResponse;
};

/**
 * Reset user password
 */
export const resetUserPassword = async (
  username: string,
  newPassword: string
): Promise<void> => {
  await client.request({
    method: 'POST',
    url: `/rogerdoc/api/dashboard/clinicians/${username}/reset`,
    query: { new_password: newPassword },
    throwOnError: true,
  });
};

/**
 * Deactivate user account
 */
export const deactivateUser = async (username: string): Promise<void> => {
  await client.request({
    method: 'POST',
    url: `/rogerdoc/api/dashboard/clinicians/${username}/deactivate`,
    throwOnError: true,
  });
};

// QA API Functions

export interface QASearchParams {
  patient_id?: string;
  patient_first_name?: string;
  patient_last_name?: string;
  start_date?: string;
  end_date?: string;
  qa_status?: string;
}

/**
 * Search QA visits
 */
export const searchQAVisits = async (
  searchParams: QASearchParams = {}
): Promise<QAVisit[]> => {
  const queryParams = new URLSearchParams();

  if (searchParams.patient_id?.trim()) {
    queryParams.append('patient_id', searchParams.patient_id.trim());
  }

  if (searchParams.patient_first_name?.trim()) {
    queryParams.append(
      'patient_first_name',
      searchParams.patient_first_name.trim()
    );
  }

  if (searchParams.patient_last_name?.trim()) {
    queryParams.append(
      'patient_last_name',
      searchParams.patient_last_name.trim()
    );
  }

  if (searchParams.start_date) {
    queryParams.append('start_date', searchParams.start_date);
  }

  if (searchParams.end_date) {
    queryParams.append('end_date', searchParams.end_date);
  }

  if (searchParams.qa_status?.trim()) {
    const backendStatuses = mapQAStatusFilterToBackend(
      searchParams.qa_status.trim()
    );
    backendStatuses.forEach(status => {
      queryParams.append('qa_statuses', status);
    });
  }

  const queryString = queryParams.toString();
  const url = `/rogerdoc/api/dashboard/patient-selector/qa/find-visits${queryString ? `?${queryString}` : ''}`;

  const response = await client.request({
    method: 'POST',
    url,
  });

  return response.data as QAVisit[];
};

/**
 * Get visit HnP PDFs
 */
export const getVisitHnps = async (visitId: string): Promise<unknown> => {
  const response = await client.request({
    method: 'GET',
    url: `/rogerdoc/api/dashboard/visits/${visitId}/hnps`,
  });

  return response.data;
};

/**
 * Get visit data for QA
 */
export const getVisitData = async (
  visitId: string
): Promise<VisitDataResponse | undefined> => {
  const response = await client.request<VisitDataResponse>({
    method: 'GET',
    url: `/rogerdoc/api/dashboard/visits/${visitId}`,
  });

  return response.data;
};

// Coding API Functions

export type SeverityType = '0' | '1' | '2' | '3' | '4';

export enum OE {
  ONSET = 'Onset',
  EXACERBATION = 'Exacerbation',
}

export interface Diagnosis {
  name: string;
  desc: string;
  severity: SeverityType | null;
  oe: OE | null;
  onset_date: string | null;
}

export interface RawDiagnosis {
  diagnosis: Diagnosis;
  reasoning: string;
}

export enum EMRSource {
  ROGER_EMR = 'rogeremr',
  WELLSKY = 'wellsky',
  HCHB = 'hchb',
  OTHER = 'other',
}

export enum MedicalCodingOverwritePolicy {
  NO_OVERWRITE = 'NO_OVERWRITE',
  OVERWRITE_WITH_SCRAPE = 'OVERWRITE_WITH_SCRAPE',
  OVERWRITE_WITHOUT_SCRAPE = 'OVERWRITE_WITHOUT_SCRAPE',
  // Force rerun — reuse existing documents, skip the F2F/HHO existence hard-cut
  OVERWRITE_WITHOUT_SCRAPE_SKIP_F2F_HHO_EXIST_CHECK = 'OVERWRITE_WITHOUT_SCRAPE_SKIP_F2F_HHO_EXIST_CHECK',
}

export type WellskyOasisVisitType =
  | 'SOC'
  | 'SOCPT'
  | 'SOCOT'
  | 'SOCST'
  | 'ROC'
  | 'ROCPT'
  | 'ROCOT'
  | 'ROCST'
  | 'REC'
  | 'RECPT'
  | 'RECOT'
  | 'RECST'
  | 'DC'
  | 'DCPT'
  | 'DCOT'
  | 'DCST'
  | 'FUP'
  | 'FUPPT'
  | 'FUPOT'
  | 'FUPST'
  | 'TF'
  | 'TFPT'
  | 'TFOT'
  | 'TFST'
  | 'DD'
  | 'DDPT'
  | 'DDOT'
  | 'DDST'
  | 'SNAE';

export interface PDFPaths {
  visit_documents: string[];
  intake_documents: string[];
  previous_visit_documents: string[];
  new_unconsidered_documents: string[];
}

export interface OtherEMRSpecificData {
  pdf_paths: PDFPaths;
  episode_start_date: string;
}

export interface CodingVisitDataBase {
  patient_id: string;
  visit_type: WellskyOasisVisitType;
  visit_date_date: string;
  patient_first_name: string;
  patient_last_name: string;
  nurse_first_name: string;
  nurse_last_name: string;
  location?: string | null;
  branch_id?: string | null;
}

export interface CodingVisit {
  diagnosis_id: string;
  visit: {
    patient_id: string;
    patient_first_name: string;
    patient_last_name: string;
    visit_date_date: string;
    visit_type: string;
    nurse_username: string;
    nurse_last_name?: string;
    nurse_first_name?: string;
    location?: string;
    branch_id?: string | null;
  };
  status: string;
  post_coding_checks?: {
    [check_name: string]: {
      active: boolean;
      severity: string;
      title: string;
    };
  };
  medical_data?: MedicalData;
  primary_diagnosis?: RawDiagnosis;
  tags?: CodingTags;
  scrape_datetime?: string | null;
}

export interface CodingFindVisitsResponse {
  items: CodingVisit[];
  total: number;
  page_size: number;
  next_cursor: string | null;
}

export interface ClinicBranchFilter {
  clinic: string;
  branches: string[];
}

export interface CodingSearchParams {
  phrase?: string;
  start_date?: string;
  end_date?: string;
  coding_status?: string[];
  visit_types?: string[];
  clinic_branches?: ClinicBranchFilter[];
  tags?: CodingTags;
  cursor?: string;
  page_size?: number;
}

/**
 * Search coding visits
 */
export const searchCodingVisits = async (
  searchParams: CodingSearchParams = {}
): Promise<CodingFindVisitsResponse> => {
  const body: Record<
    string,
    string | number | string[] | CodingTags | ClinicBranchFilter[]
  > = {};

  if (searchParams.phrase?.trim()) {
    body.phrase = searchParams.phrase.trim();
  }

  if (searchParams.start_date) {
    body.start_date = searchParams.start_date;
  }

  if (searchParams.end_date) {
    body.end_date = searchParams.end_date;
  }

  if (searchParams.coding_status?.length) {
    body.coding_status = searchParams.coding_status;
  }

  if (searchParams.visit_types?.length) {
    body.visit_types = searchParams.visit_types;
  }

  if (searchParams.clinic_branches?.length) {
    body.clinic_branches = searchParams.clinic_branches;
  }

  if (searchParams.tags && Object.keys(searchParams.tags).length > 0) {
    body.tags = searchParams.tags;
  }

  if (searchParams.cursor) {
    body.cursor = searchParams.cursor;
  }

  if (searchParams.page_size != null) {
    body.page_size = searchParams.page_size;
  }

  const response = await client.request({
    method: 'POST',
    url: '/rogerdoc/api/dashboard/patient-selector/coding/find-visits',
    body,
  });

  return response.data as CodingFindVisitsResponse;
};

/**
 * Bulk update tags for coding visits
 */
export type BulkTagActionType =
  | 'mark_f2f_attached'
  | 'mark_f2f_not_attached'
  | 'mark_order_attached'
  | 'mark_order_not_attached';

export interface BulkUpdateTagsRequest {
  diagnosis_ids: string[];
  tags: CodingTags;
  action?: { type: BulkTagActionType };
  dashboard_user_email?: string;
}

export interface BulkUpdateTagsResponse {
  updated_count: number;
}

export const bulkUpdateCodingTags = async (
  request: BulkUpdateTagsRequest
): Promise<BulkUpdateTagsResponse> => {
  const response = await client.request({
    method: 'PATCH',
    url: '/rogerdoc/api/dashboard/patient-selector/coding/bulk-update-tags',
    body: request,
  });

  return response.data as BulkUpdateTagsResponse;
};

/**
 * Get coding data for a diagnosis
 */
export const getCodingData = async (diagnosisId: string): Promise<unknown> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/visits/${diagnosisId}/coding/load/data`,
    body: {},
  });

  return response.data;
};

/**
 * A Roger Docs request that came back with an error status.
 *
 * Carries the generated client's `{ error, response }` so the approved helpers
 * in `utils/apiErrorDetail` can read the status and the structured `detail`
 * (e.g. `getRefusedDiagnoses` for a 422 naming invalid codes). The message is
 * for logs only; callers show their own copy.
 */
export class RogerDocRequestError extends Error {
  readonly error: unknown;
  readonly response: unknown;

  constructor(
    message: string,
    result: { error?: unknown; response?: unknown }
  ) {
    super(message);
    this.name = 'RogerDocRequestError';
    this.error = result.error;
    this.response = result.response;
  }
}

/**
 * Search diagnosis codes.
 *
 * `asOf` (YYYY-MM-DD) is the record's date — on the coding page, the coding
 * as-of date (M0090, or the visit date) from the top-level `codes_as_of` of
 * `coding/load/data` — so Roger Docs offers the codes valid that day. A
 * Roger Docs without date-aware search ignores the unknown query parameter.
 */
export const searchDiagnosisCodes = async (
  searchText: string,
  { asOf }: { asOf?: string } = {}
): Promise<unknown> => {
  const params = new URLSearchParams({ search_text: searchText });
  if (asOf) params.append('as_of', asOf);
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/search/diagnosis?${params.toString()}`,
    body: {},
  });
  // The client does not throw on error responses. A refusal (e.g. no code set
  // for this date) must reach the caller's catch, not read as "no results".
  if (response.error) {
    throw new RogerDocRequestError(
      'Failed to search diagnosis codes',
      response
    );
  }
  return response.data;
};

/**
 * Recalculate payment information
 */
export const recalculatePaymentInfo = async (
  diagnosisId: string,
  primaryDiagnosisName: string,
  secondaryDiagnosesNames: string[]
): Promise<{
  caseMixData: CaseMixData;
  caseMixData2ndPeriod?: CaseMixData;
}> => {
  const params = new URLSearchParams();
  params.append('primary_diagnosis_name', primaryDiagnosisName);

  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/${diagnosisId}/recalculate/case-mix?${params.toString()}`,
    body: secondaryDiagnosesNames,
  });

  // API returns { case_mix_data, case_mix_data_2nd_period? }. The second period
  // is present only when the medical_coding_show_2nd_30_day_billing_period flag
  // is enabled and the first period is "early".
  const data = response.data as
    | {
        case_mix_data?: CaseMixData;
        case_mix_data_2nd_period?: CaseMixData;
      }
    | undefined;
  // The client does not throw on error responses; without this check a failed
  // recalculation would blank the case mix instead of reaching callers' catch.
  if (response.error || !data?.case_mix_data) {
    throw new RogerDocRequestError(
      'Failed to recalculate payment information',
      response
    );
  }
  return {
    caseMixData: data.case_mix_data,
    caseMixData2ndPeriod: data.case_mix_data_2nd_period,
  };
};

/**
 * Get OASIS analysis errors
 */
export const getOasisAnalysisErrors = async (
  visitId: string
): Promise<unknown> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/visits/${visitId}/oasis/analysis/errors/fetch/dashboard`,
    body: {},
  });

  return (response.data as { result?: unknown })?.result || response.data;
};

/**
 * Get OASIS analysis suggested fixes
 */
export const getOasisAnalysisSuggestedFixes = async (
  visitId: string
): Promise<unknown> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/visits/${visitId}/oasis/analysis/fixes/fetch/dashboard`,
    body: {},
  });

  return (response.data as { result?: unknown })?.result || response.data;
};

/**
 * Get OASIS analysis fixes log
 */
export const getOasisAnalysisFixesLog = async (
  visitId: string
): Promise<unknown> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/visits/${visitId}/oasis/analysis/fixes/log`,
    body: {},
  });

  return response.data;
};

/**
 * Run OASIS validator
 */
export const runOasisValidator = async (visitId: string): Promise<unknown> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/visits/${visitId}/oasis/analysis/validator/run/dashboard`,
    body: {},
  });

  return (response.data as { result?: unknown })?.result || response.data;
};

/**
 * Download PDF
 */
export const downloadPdf = async (pdfKey: string): Promise<Blob> => {
  const response = await client.request({
    method: 'POST',
    baseURL: API_BASE_URL,
    url: `/rogerdoc/api/pdf-download?pdf_key=${encodeURIComponent(pdfKey)}`,
    body: {},
    responseType: 'blob',
    throwOnError: true,
  });

  const blob = response.data;
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('PDF download returned empty response');
  }

  // Upstream errors arrive as JSON blobs when responseType is 'blob'.
  if (blob.type.includes('json')) {
    const detail = await blob.text();
    throw new Error(detail || 'PDF download failed');
  }

  return blob;
};

/**
 * Start QA analysis for a visit
 */
export const startQAAnalysis = async (
  visitId: string,
  useStructuredRules: boolean = true
): Promise<{ qa_id: string; job_id?: string; status: string }> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/dashboard/visits/${visitId}/qa/start`,
    body: { use_structured_qa_rules: useStructuredRules },
  });

  return response.data as {
    qa_id: string;
    job_id?: string;
    status: string;
  };
};

/**
 * Get latest QA analysis status for a visit
 */
export const getLatestQAStatus = async (visitId: string): Promise<unknown> => {
  try {
    const response = await client.request({
      method: 'GET',
      url: `/rogerdoc/api/dashboard/visits/${visitId}/qa/latest-status`,
    });
    return response.data;
  } catch {
    // Return null if no QA exists (404)
    return null;
  }
};

/**
 * Get all QA analysis data for a visit
 */
export const getAllVisitQAData = async (
  visitId: string
): Promise<unknown[]> => {
  try {
    const response = await client.request({
      method: 'GET',
      url: `/rogerdoc/api/dashboard/visits/${visitId}/qa`,
    });
    return (response.data as unknown[]) || [];
  } catch {
    // Return empty array if no QA exists (404)
    return [];
  }
};

/**
 * Get adoption report data
 * Fetches visit completion report data for clinicians
 */
export const getAdoptionReport = async (
  fromDate?: Date | null,
  toDate?: Date | null
): Promise<AdoptionReportRawData[]> => {
  // Build query parameters
  const params: Record<string, string> = {};

  if (fromDate) {
    const month = String(fromDate.getMonth() + 1).padStart(2, '0');
    const day = String(fromDate.getDate()).padStart(2, '0');
    const year = fromDate.getFullYear();
    params.date_from = `${month}/${day}/${year}`; // Format as MM/DD/YYYY
  }

  if (toDate) {
    const month = String(toDate.getMonth() + 1).padStart(2, '0');
    const day = String(toDate.getDate()).padStart(2, '0');
    const year = toDate.getFullYear();
    params.date_to = `${month}/${day}/${year}`; // Format as MM/DD/YYYY
  }

  const queryString = new URLSearchParams(params).toString();
  const url = `/rogerdoc/api/dashboard/visit_completion_report${queryString ? `?${queryString}` : ''}`;

  const response = await client.request({
    method: 'POST',
    url,
    body: {},
  });

  return (response.data as AdoptionReportRawData[]) || [];
};

/**
 * Get branch names
 */
export const getBranchNames = async (): Promise<Record<string, string>> => {
  const response = await client.request({
    method: 'GET',
    url: '/rogerdoc/api/clinics/info/branch_names_by_id',
  });
  return response.data as Record<string, string>;
};

/**
 * Get adoption report data by branch
 */
export const getAdoptionReportByBranch = async (
  branchIds: string[],
  fromDate?: Date | null,
  toDate?: Date | null
): Promise<BranchReportResponse> => {
  // Build query parameters
  const params: Record<string, string> = {};

  if (fromDate) {
    const month = String(fromDate.getMonth() + 1).padStart(2, '0');
    const day = String(fromDate.getDate()).padStart(2, '0');
    const year = fromDate.getFullYear();
    params.date_from = `${month}/${day}/${year}`;
  }

  if (toDate) {
    const month = String(toDate.getMonth() + 1).padStart(2, '0');
    const day = String(toDate.getDate()).padStart(2, '0');
    const year = toDate.getFullYear();
    params.date_to = `${month}/${day}/${year}`;
  }

  const queryString = new URLSearchParams(params).toString();
  const url = `/rogerdoc/api/dashboard/visit_completion_report_by_branch${queryString ? `?${queryString}` : ''}`;

  const response = await client.request({
    method: 'POST',
    url,
    body: branchIds,
  });

  return response.data as BranchReportResponse;
};

// Worksheet Goals API Functions
import type {
  WorksheetGoal,
  WorksheetIntervention,
  ResyncResponse,
} from '../types/worksheetGoals.types';
import { MedicalData } from '@/types/medicalData.types';

/**
 * Fetch worksheet goals creation types by discipline
 * @returns Dictionary mapping discipline to list of visit types
 */
export const getWorksheetGoalsCreationTypes = async (): Promise<
  Record<string, string[]>
> => {
  const response = await client.request({
    method: 'POST',
    url: '/rogerdoc/api/worksheet-goals/creation_types/fetch',
    body: {},
  });

  return response.data as Record<string, string[]>;
};

/**
 * Fetch worksheet goals with filtering and pagination
 */
export const getWorksheetGoals = async (
  clinicKey: string | null,
  filters?: {
    goal_search_string?: string;
    discipline?: string;
    required?: boolean;
    page_size?: number;
    page_number?: number;
  }
): Promise<WorksheetGoal[]> => {
  const queryParams = new URLSearchParams();

  if (clinicKey) {
    queryParams.append('clinic_key', clinicKey);
  }

  if (filters?.goal_search_string) {
    queryParams.append('goal_search_string', filters.goal_search_string);
  }

  if (filters?.discipline && filters.discipline !== 'all_disciplines') {
    queryParams.append('discipline', filters.discipline);
  }

  if (filters?.required) {
    queryParams.append('required', 'true');
  }

  if (filters?.page_size) {
    queryParams.append('page_size', filters.page_size.toString());
  }

  if (filters?.page_number) {
    queryParams.append('page_number', filters.page_number.toString());
  }

  const queryString = queryParams.toString();
  const url = `/rogerdoc/api/worksheet-goals/goals/fetch${queryString ? `?${queryString}` : ''}`;

  const response = await client.request({
    method: 'POST',
    url,
    body: {},
  });

  return response.data as WorksheetGoal[];
};

/**
 * Get count of worksheet goals matching filters
 */
export const getWorksheetGoalsCount = async (
  clinicKey: string | null,
  filters?: {
    goal_search_string?: string;
    discipline?: string;
    required?: boolean;
  }
): Promise<number> => {
  const queryParams = new URLSearchParams();

  if (clinicKey) {
    queryParams.append('clinic_key', clinicKey);
  }

  if (filters?.goal_search_string) {
    queryParams.append('goal_search_string', filters.goal_search_string);
  }

  if (filters?.discipline && filters.discipline !== 'all_disciplines') {
    queryParams.append('discipline', filters.discipline);
  }

  if (filters?.required) {
    queryParams.append('required', 'true');
  }

  const queryString = queryParams.toString();
  const url = `/rogerdoc/api/worksheet-goals/goals/templates/count${queryString ? `?${queryString}` : ''}`;

  const response = await client.request({
    method: 'POST',
    url,
    body: {},
  });

  return response.data as number;
};

/**
 * Update a worksheet goal
 * @param goal - The goal object to update
 * @returns Updated goal object
 */
export const updateWorksheetGoal = async (
  goal: WorksheetGoal
): Promise<WorksheetGoal> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/worksheet-goals/templates/goals/update`,
    body: goal,
  });

  return response.data as WorksheetGoal;
};

/**
 * Resync worksheet goals from Wellsky
 * Will resync all clinics for the current agency
 * @returns Resync response with results
 */
export const resyncWorksheetGoals = async (): Promise<ResyncResponse> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/fetch-goals-templates-by-dict-temporal?wait_for_completion=true`,
  });

  return response.data as ResyncResponse;
};

/**
 * Fetch worksheet interventions for specific intervention keys
 * @param clinicKey - The clinic key
 * @param interventionKeys - Array of intervention keys to fetch
 * @returns Array of worksheet interventions
 */
export const getWorksheetInterventions = async (
  clinicKey: string,
  interventionKeys: string[]
): Promise<WorksheetIntervention[]> => {
  if (interventionKeys.length === 0) {
    return [];
  }

  const response = await client.request({
    method: 'POST',
    body: interventionKeys,
    url: `/rogerdoc/api/worksheet-goals/interventions/fetch`,
    query: {
      clinic_key: clinicKey,
    },
  });

  return response.data as WorksheetIntervention[];
};

/**
 * Create a new diagnosis state
 */
export const createDiagnosisState = async (
  diagnosisId: string,
  stateData: CreateStateRequest
): Promise<SavedDiagnosisState> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/${diagnosisId}/states`,
    body: stateData,
  });

  // Check if the response is an error. The body stays on the error, not in
  // its message, so a 422 naming invalid codes can be shown in our own words.
  if ('error' in response && response.error) {
    throw new RogerDocRequestError(
      'Failed to create diagnosis state',
      response
    );
  }

  // Validate that we have data
  if (!response.data) {
    throw new Error(
      'Failed to create diagnosis state: No data returned from server'
    );
  }

  return response.data as SavedDiagnosisState;
};

/**
 * Update an existing diagnosis state
 */
export const updateDiagnosisState = async (
  diagnosisId: string,
  stateId: string,
  stateData: CreateStateRequest
): Promise<SavedDiagnosisState> => {
  const response = await client.request({
    method: 'PUT',
    url: `/rogerdoc/api/coding/${diagnosisId}/states/${stateId}`,
    body: stateData,
  });

  // The client does not throw on an error status; without this a refused save
  // read as a successful one.
  if (response.error) {
    throw new RogerDocRequestError(
      'Failed to update diagnosis state',
      response
    );
  }
  return response.data as SavedDiagnosisState;
};

/**
 * Delete a diagnosis state
 */
export const deleteDiagnosisState = async (
  diagnosisId: string,
  stateId: string,
  dashboardUserEmail?: string
): Promise<void> => {
  const query: Record<string, string> = {};
  if (dashboardUserEmail) {
    query.dashboard_user_email = dashboardUserEmail;
  }

  await client.request({
    method: 'DELETE',
    url: `/rogerdoc/api/coding/${diagnosisId}/states/${stateId}`,
    query,
  });
};

/**
 * Push coding state to EMR
 */
export const pushToEmr = async (
  emrType: string = 'wellsky',
  diagnosisId: string,
  codingStateId?: string,
  dashboardUserEmail?: string
): Promise<void> => {
  const query: Record<string, string> = {};

  if (codingStateId) {
    query.coding_state_id = codingStateId;
  }

  if (dashboardUserEmail) {
    query.dashboard_user_email = dashboardUserEmail;
  }

  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/push/${emrType}/${diagnosisId}`,
    query,
  });
  // A refused push (e.g. a 422 for codes not valid for a claim) must not start
  // polling for a workflow that was never started.
  if (response.error) {
    throw new RogerDocRequestError('Failed to push coding to EMR', response);
  }
};

/**
 * Get push coding workflow status
 */
export const getPushCodingStatus = async (
  diagnosisId: string,
  codingStateId?: string
): Promise<PushCodingStatusResponse> => {
  const response = await client.request({
    method: 'GET',
    url: `/rogerdoc/api/coding/push/status/${diagnosisId}`,
    ...(codingStateId
      ? {
          query: {
            codingStateId,
          },
        }
      : {}),
  });

  return response.data as PushCodingStatusResponse;
};

/**
 * Get filling history for a diagnosis
 */
export interface FillingHistoryItem {
  id: string;
  diagnosis_id: string;
  coding_state_id: string | null;
  status: string;
  start_time: string;
  end_time: string;
  workflow_id?: string;
  primary_diagnosis?: PrimaryDiagnosis;
  secondary_diagnoses?: SecondaryDiagnoses;
  previous_primary_diagnosis?: PrimaryDiagnosis;
  previous_secondary_diagnoses?: SecondaryDiagnoses;
  error_type?: string | null;
}

export const getFillingHistory = async (
  diagnosisId: string
): Promise<FillingHistoryItem[]> => {
  const response = await client.request({
    method: 'GET',
    url: `/rogerdoc/api/coding/${diagnosisId}/filling-history`,
  });

  if (!Array.isArray(response.data)) {
    console.warn('getFillingHistory response is not an array:', response.data);
    return [];
  }

  return response.data as FillingHistoryItem[];
};

/**
 * Run medical coding extraction
 */
export const runCodingDiagnosis = async (
  emrSource: EMRSource,
  codingVisitData: CodingVisitDataBase,
  overwriteOldResults: MedicalCodingOverwritePolicy = MedicalCodingOverwritePolicy.NO_OVERWRITE,
  otherEMRSpecificData?: OtherEMRSpecificData
): Promise<{ success: boolean; job_id?: string; message: string }> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/start/full/diagnoses-generation`,
    query: {
      emr_source: emrSource,
      overwrite_old_results: overwriteOldResults,
    },
    body: {
      coding_visit_data: codingVisitData,
      other_EMR_specific_data: otherEMRSpecificData,
    },
  });

  return response.data as {
    success: boolean;
    job_id?: string;
    message: string;
  };
};

/**
 * Rerun medical coding extraction
 */
export const rerunCodingDiagnosis = async (
  diagnosisId: string,
  emrSource: EMRSource,
  overwriteExisting: MedicalCodingOverwritePolicy = MedicalCodingOverwritePolicy.OVERWRITE_WITH_SCRAPE,
  dashboardUserEmail?: string
): Promise<{ success: boolean; job_id?: string; message: string }> => {
  const query: Record<string, string> = {
    diagnosis_id: diagnosisId,
    emr_source: emrSource as string,
    overwrite_existing: overwriteExisting as string,
  };

  if (dashboardUserEmail) {
    query.dashboard_user_email = dashboardUserEmail;
  }

  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/coding/start/full/diagnoses-generation/rerun`,
    query,
  });

  return response.data as {
    success: boolean;
    job_id?: string;
    message: string;
  };
};

/**
 * Clinic info from RogerDoc
 */
export interface RogerDocClinicBranch {
  id: string;
  name: string;
}

export interface RogerDocClinic {
  id: string;
  name: string;
  branches?: RogerDocClinicBranch[];
}

/** Rejects on an error response, so callers can keep the clinics they have. */
export const getRogerDocClinics = async (
  agencyId: string
): Promise<RogerDocClinic[]> => {
  const response = await client.request({
    method: 'GET',
    url: `/rogerdoc/api/clinics/${agencyId}`,
    throwOnError: true,
  });

  if (!Array.isArray(response.data)) {
    throw new Error('Failed to load clinics');
  }
  return response.data as RogerDocClinic[];
};

// Customizations Tab API Functions

/**
 * Vital Signs Parameters (VSP) data structure
 */
export interface VSPData {
  c485np_temphigh: number | null;
  c485np_templow: number | null;
  c485np_pulsehigh: number | null;
  c485np_pulselow: number | null;
  c485np_resphigh: number | null;
  c485np_resplow: number | null;
  c485np_syshigh: number | null;
  c485np_syslow: number | null;
  c485np_diashigh: number | null;
  c485NP_DiasLow: number | null;
  c485np_02stat: number | null;
  c485np_fastbslevelgt: number | null;
  c485np_fastbslevellt: number | null;
  c485np_randombslevelgt: number | null;
  c485np_randombslevellt: number | null;
}

/**
 * Narrative Templates response structure
 */
export interface NarrativeTemplatesResponse {
  existing_narrative_templates: Record<string, string>;
  KINNSER_NARRATIVE_QIDS: {
    [qidType: string]: {
      default?: { section: string };
      [discipline: string]: { section: string } | undefined;
    };
  };
}

/**
 * Get VSP data for an agency
 */
export const getVSP = async (): Promise<VSPData> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/vsp`,
    body: {},
  });

  return response.data as VSPData;
};

/**
 * Update VSP data for an agency
 */
export const updateVSP = async (vspData: VSPData): Promise<VSPData> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/vsp/update`,
    body: vspData,
  });

  return response.data as VSPData;
};

/**
 * Fetch narrative templates for an agency
 */
export const fetchNarrativeTemplates =
  async (): Promise<NarrativeTemplatesResponse> => {
    const response = await client.request({
      method: 'POST',
      url: `/rogerdoc/api/narrative-templates`,
      body: {},
    });

    return response.data as NarrativeTemplatesResponse;
  };

/**
 * Update narrative templates for an agency
 */
export const updateNarrativeTemplates = async (
  templates: Record<string, string>
): Promise<void> => {
  await client.request({
    method: 'POST',
    url: `/rogerdoc/api/narrative-templates/update`,
    body: templates,
  });
};

/**
 * Fetch feature flags from roger-docs for the currently authenticated user.
 */
/** Rejects on an error response, so callers can keep the flags they have. */
export const getRogerDocFeatureFlags = async (): Promise<
  Record<string, boolean>
> => {
  const response = await client.request({
    method: 'GET',
    url: `/rogerdoc/feature_flags`,
    throwOnError: true,
  });
  return (response.data ?? {}) as Record<string, boolean>;
};

/**
 * Goals progress validation config types
 */
export interface P2GValidationConfigs {
  require_viewing: boolean;
  require_goals_progress_when_interventions_performed: boolean;
  require_progress_note_when_score: boolean;
  require_performance_note: boolean;
  min_goals_performed: number;
  min_interventions_performed: number;
}

/**
 * Get goals progress validation config for the current agency.
 */
export const getGoalsProgressValidationConfig =
  async (): Promise<P2GValidationConfigs> => {
    const response = await client.request({
      method: 'GET',
      url: `/rogerdoc/goals/progress-validation-config`,
    });
    return response.data as P2GValidationConfigs;
  };

/**
 * Set goals progress validation config for the current agency.
 */
export const setGoalsProgressValidationConfig = async (
  config: P2GValidationConfigs
): Promise<P2GValidationConfigs> => {
  const response = await client.request({
    method: 'POST',
    url: `/rogerdoc/goals/progress-validation-config`,
    body: config,
  });
  return response.data as P2GValidationConfigs;
};
